import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Eye, MousePointer, Pause, Play, RefreshCw, Pencil, Trash2,
  Upload, Check, Clock, X as XIcon, AlertTriangle, Globe, MapPin, DollarSign, BarChart3,
  Target, Zap, Users, Award, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import PublicFeatureNav from '../components/PublicFeatureNav';
import PublicLogo from '../components/PublicLogo';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import AdCarousel from '../components/AdCarousel';

const API = process.env.REACT_APP_BACKEND_URL;

const PLACEMENTS = [
  { id: 'dashboard_banner', label: 'Dashboard Banner (728×90)', desc: 'Shown at the bottom of the main dashboard' },
  { id: 'sidebar', label: 'Sidebar (300×250)', desc: 'Displayed alongside hive details' },
  { id: 'find_honey_sponsored', label: 'Find Honey — Sponsored Listing', desc: 'Premium card on the public marketplace' },
  { id: 'mobile_banner', label: 'Mobile Banner (320×50)', desc: 'Compact banner for mobile users' },
];

const GEO_OPTIONS = [
  { id: 'local', label: 'Local (50km radius)' },
  { id: 'state', label: 'State / Region' },
  { id: 'national', label: 'National' },
  { id: 'global', label: 'Global' },
];

const DURATION_OPTIONS = [
  { months: 1, label: '1 Month' },
  { months: 3, label: '3 Months (10% off)' },
  { months: 6, label: '6 Months (20% off)' },
  { months: 12, label: '12 Months (30% off)' },
];

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  pending_review: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-stone-100 text-stone-500',
};

const STATUS_LABELS = {
  active: 'Live', paused: 'Paused', pending_review: 'Under Review',
  rejected: 'Rejected', expired: 'Expired',
};

const emptyForm = {
  title: '', advertiser_name: '', advertiser_url: '', image_url: '', description: '',
  placement: 'dashboard_banner', geo_target: 'global', geo_country: '', geo_state: '',
  duration_months: 1,
};

const AdvertiserLanding = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen honeycomb-bg">
      {/* Header */}
      <header className="backdrop-blur-xl bg-white/80 border-b border-amber-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <PublicLogo size="header" />
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" className="text-stone-600 hover:text-amber-700 text-sm font-medium">Log In</Button></Link>
            <Link to="/register"><Button className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl px-5" data-testid="register-cta">Sign Up Free</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-20 border-b border-amber-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-start gap-6 justify-center">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900 mb-4">Advertise on <span className="text-amber-600">Thrive</span></h1>
              <p className="text-lg text-stone-600 max-w-2xl mx-auto mb-8">
                Reach thousands of beekeepers across Australia. Promote your equipment, supplies, or services directly to an engaged community.
              </p>
            </div>
            <PublicLogo size="hero" className="hidden sm:block flex-shrink-0 mt-2" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl px-8 h-12 text-base" data-testid="get-started-btn">
                <Zap className="h-5 w-5 mr-2" /> Get Started — It's Free to Sign Up
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-100 rounded-xl px-6 h-12">
                Already have an account? Log In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-stone-900 text-center mb-10">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Users, title: '1. Create an Account', desc: 'Sign up for a free Thrive account. Any user can advertise — no special account needed.' },
            { icon: Target, title: '2. Build Your Ad', desc: 'Use our self-service builder to create a banner ad. Choose placement, targeting, and duration.' },
            { icon: Award, title: '3. Go Live', desc: 'Submit for review. Once approved, your ad appears across the Thrive platform within 12 hours.' },
          ].map(item => (
            <div key={item.title} className="bg-white rounded-2xl p-6 border border-stone-200 text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <item.icon className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">{item.title}</h3>
              <p className="text-sm text-stone-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ad Placements with Mock Previews */}
      <section className="bg-white/80 border-t border-amber-100 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-stone-900 text-center mb-3">Ad Placements</h2>
          <p className="text-stone-500 text-center mb-10 max-w-xl mx-auto">See exactly where your ads appear across the Thrive platform</p>

          {/* Dashboard Banner Mock */}
          <div className="mb-10">
            <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-amber-600" />
              Dashboard Banner (728×90)
            </h3>
            <div className="bg-stone-100 rounded-2xl border border-stone-200 p-4">
              {/* Mini dashboard mockup */}
              <div className="bg-white rounded-lg border border-stone-200 p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 bg-amber-100 rounded" />
                  <div className="h-3 bg-stone-200 rounded w-24" />
                  <div className="ml-auto flex gap-2">
                    <div className="h-3 bg-stone-100 rounded w-12" />
                    <div className="h-3 bg-stone-100 rounded w-12" />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="bg-stone-50 rounded-lg p-2">
                      <div className="h-2 bg-stone-200 rounded w-8 mb-1" />
                      <div className="h-4 bg-stone-200 rounded w-12" />
                    </div>
                  ))}
                </div>
              </div>
              {/* The ad banner */}
              <div className="bg-gradient-to-r from-amber-500 via-amber-400 to-orange-400 rounded-xl p-4 flex items-center justify-between border-2 border-dashed border-amber-600 relative">
                <span className="absolute -top-2.5 left-3 bg-amber-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">YOUR AD HERE</span>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/30 rounded-lg flex items-center justify-center">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Premium Bee Equipment Co.</p>
                    <p className="text-amber-100 text-xs">Quality hives, frames & tools — Free shipping over $100</p>
                  </div>
                </div>
                <div className="bg-white text-amber-700 font-bold text-xs px-4 py-2 rounded-lg">Shop Now</div>
              </div>
              <p className="text-[10px] text-stone-400 text-center mt-2">728×90 — Shown at the bottom of every dashboard session</p>
            </div>
          </div>

          {/* Sidebar + Sponsored Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            {/* Sidebar Mock */}
            <div>
              <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                <Target className="h-4 w-4 text-amber-600" />
                Sidebar Ad (300×250)
              </h3>
              <div className="bg-stone-100 rounded-2xl border border-stone-200 p-4">
                <div className="flex gap-3">
                  {/* Content mock */}
                  <div className="flex-1 bg-white rounded-lg border border-stone-200 p-3">
                    <div className="h-3 bg-stone-200 rounded w-24 mb-2" />
                    <div className="space-y-1">
                      {[1,2,3].map(i => <div key={i} className="h-2 bg-stone-100 rounded w-full" />)}
                    </div>
                    <div className="h-20 bg-stone-50 rounded mt-2" />
                  </div>
                  {/* Sidebar ad */}
                  <div className="w-36 border-2 border-dashed border-amber-500 rounded-xl bg-gradient-to-b from-green-500 to-green-700 p-3 flex flex-col items-center justify-center text-center relative">
                    <span className="absolute -top-2 left-2 bg-amber-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">AD</span>
                    <div className="w-8 h-8 bg-white/30 rounded-full mb-2" />
                    <p className="text-white font-bold text-xs leading-tight">Oz Honey Co.</p>
                    <p className="text-green-100 text-[9px] mt-0.5">Pure Australian Manuka</p>
                    <div className="bg-white text-green-700 font-bold text-[10px] px-3 py-1 rounded mt-2">Buy Now</div>
                  </div>
                </div>
                <p className="text-[10px] text-stone-400 text-center mt-2">300×250 — Alongside hive detail pages</p>
              </div>
            </div>

            {/* Find Honey Sponsored */}
            <div>
              <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                <Award className="h-4 w-4 text-amber-600" />
                Sponsored Listing
              </h3>
              <div className="bg-stone-100 rounded-2xl border border-stone-200 p-4">
                {/* Marketplace mock */}
                <div className="space-y-2">
                  <div className="bg-white rounded-lg border border-stone-200 p-2 flex items-center gap-2 opacity-50">
                    <div className="w-8 h-8 bg-stone-200 rounded-full" />
                    <div>
                      <div className="h-2.5 bg-stone-200 rounded w-20" />
                      <div className="h-2 bg-stone-100 rounded w-14 mt-1" />
                    </div>
                  </div>
                  {/* Sponsored card */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border-2 border-dashed border-amber-500 p-3 flex items-center gap-3 relative">
                    <span className="absolute -top-2 right-3 bg-amber-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full">SPONSORED</span>
                    <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center">
                      <Award className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-stone-800 text-sm">Your Business Name</p>
                      <p className="text-xs text-stone-500">Featured at the top of honey search results</p>
                      <div className="flex items-center gap-1 mt-1">
                        {[1,2,3,4,5].map(s => <span key={s} className="text-amber-400 text-xs">★</span>)}
                        <span className="text-[10px] text-stone-400">Promoted</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border border-stone-200 p-2 flex items-center gap-2 opacity-50">
                    <div className="w-8 h-8 bg-stone-200 rounded-full" />
                    <div>
                      <div className="h-2.5 bg-stone-200 rounded w-24" />
                      <div className="h-2 bg-stone-100 rounded w-16 mt-1" />
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-stone-400 text-center mt-2">Premium listing on Find Local Honey</p>
              </div>
            </div>
          </div>

          {/* Mobile Banner */}
          <div className="max-w-xs mx-auto">
            <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
              <Globe className="h-4 w-4 text-amber-600" />
              Mobile Banner (320×50)
            </h3>
            <div className="bg-stone-100 rounded-2xl border border-stone-200 p-3">
              {/* Phone frame */}
              <div className="bg-white rounded-2xl border-2 border-stone-300 p-2 max-w-[200px] mx-auto">
                <div className="h-2 w-10 bg-stone-200 rounded-full mx-auto mb-2" />
                <div className="space-y-1 mb-2">
                  <div className="h-2 bg-stone-100 rounded w-full" />
                  <div className="h-2 bg-stone-100 rounded w-3/4" />
                  <div className="h-8 bg-stone-50 rounded" />
                </div>
                {/* Mobile ad banner */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-2 flex items-center gap-2 border border-dashed border-blue-400 relative">
                  <span className="absolute -top-1.5 left-1 bg-amber-500 text-white text-[7px] font-bold px-1 py-0.5 rounded-full">AD</span>
                  <div className="w-5 h-5 bg-white/30 rounded" />
                  <div className="flex-1">
                    <p className="text-white font-bold text-[9px]">BeeSupply.com.au</p>
                    <p className="text-blue-100 text-[7px]">20% off all frames</p>
                  </div>
                  <div className="bg-white text-blue-700 font-bold text-[8px] px-2 py-0.5 rounded">Visit</div>
                </div>
              </div>
              <p className="text-[10px] text-stone-400 text-center mt-2">320×50 — Sticky footer on mobile views</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-stone-900 text-center mb-3">Transparent Pricing</h2>
        <p className="text-stone-500 text-center mb-10 max-w-xl mx-auto">Simple, upfront pricing. No hidden fees. Choose your placement, reach, and duration.</p>

        {/* Placement Prices */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { name: 'Dashboard Banner', size: '728×90', price: 50, icon: BarChart3, color: 'from-amber-50 to-amber-100 border-amber-200', accent: 'text-amber-700', reach: 'Every dashboard session' },
            { name: 'Sidebar Ad', size: '300×250', price: 35, icon: Target, color: 'from-green-50 to-green-100 border-green-200', accent: 'text-green-700', reach: 'Hive detail pages' },
            { name: 'Sponsored Listing', size: 'Featured', price: 75, icon: Award, color: 'from-purple-50 to-purple-100 border-purple-200', accent: 'text-purple-700', reach: 'Top of honey search' },
            { name: 'Mobile Banner', size: '320×50', price: 25, icon: Globe, color: 'from-blue-50 to-blue-100 border-blue-200', accent: 'text-blue-700', reach: 'Sticky mobile footer' },
          ].map(p => (
            <div key={p.name} className={`bg-gradient-to-b ${p.color} rounded-2xl border p-5 text-center`}>
              <p.icon className={`h-7 w-7 ${p.accent} mx-auto mb-3`} />
              <h3 className="font-bold text-stone-800 text-sm">{p.name}</h3>
              <p className="text-[11px] text-stone-400 mb-2">{p.size}</p>
              <div className={`text-3xl font-bold ${p.accent}`}>${p.price}<span className="text-sm font-normal text-stone-400">/mo</span></div>
              <p className="text-[10px] text-stone-500 mt-2">{p.reach}</p>
            </div>
          ))}
        </div>

        {/* Discounts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Reach / Geo Targeting */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
              <Globe className="h-4 w-4 text-amber-600" />
              Reach Multiplier
            </h3>
            <div className="space-y-2">
              {[
                { label: 'Local (single region)', mult: '0.5×', price: 'Half price', color: 'bg-green-50 text-green-700' },
                { label: 'State-wide', mult: '0.75×', price: '25% off', color: 'bg-blue-50 text-blue-700' },
                { label: 'National', mult: '1.0×', price: 'Base price', color: 'bg-stone-50 text-stone-700' },
                { label: 'Global', mult: '1.5×', price: '50% premium', color: 'bg-amber-50 text-amber-700' },
              ].map(g => (
                <div key={g.label} className={`flex items-center justify-between px-3 py-2 rounded-lg ${g.color}`}>
                  <span className="text-sm font-medium">{g.label}</span>
                  <div className="text-right">
                    <span className="text-xs font-bold">{g.mult}</span>
                    <span className="text-[10px] text-stone-400 ml-1">({g.price})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Duration Discounts */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-600" />
              Duration Discounts
            </h3>
            <div className="space-y-2">
              {[
                { label: '1 Month', discount: 'No discount', color: 'bg-stone-50 text-stone-600', save: '' },
                { label: '3 Months', discount: '10% off', color: 'bg-green-50 text-green-700', save: 'Save 10%' },
                { label: '6 Months', discount: '20% off', color: 'bg-blue-50 text-blue-700', save: 'Save 20%' },
                { label: '12 Months', discount: '30% off', color: 'bg-amber-50 text-amber-700', save: 'Best Value' },
              ].map(d => (
                <div key={d.label} className={`flex items-center justify-between px-3 py-2 rounded-lg ${d.color}`}>
                  <span className="text-sm font-medium">{d.label}</span>
                  <div className="text-right">
                    <span className="text-xs font-bold">{d.discount}</span>
                    {d.save && <span className="text-[10px] ml-1 bg-white/60 px-1.5 py-0.5 rounded-full font-semibold">{d.save}</span>}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-stone-400 mt-3 text-center">
              Example: Dashboard Banner, National, 6 months = $50 × 1.0 × 0.8 = <strong>$40/mo</strong>
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-stone-900 text-white py-16">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h3 className="text-2xl font-bold mb-3">Ready to Reach Beekeepers?</h3>
          <p className="text-stone-400 mb-6">Create your account, build an ad in minutes, and start connecting with your audience today.</p>
          <Link to="/register">
            <Button className="bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl px-8 h-12 text-base">
              Sign Up & Create Your First Ad
            </Button>
          </Link>
        </div>
      </section>

      <footer className="bg-stone-800 text-stone-400 text-center py-6 text-sm">
        <p>&copy; {new Date().getFullYear()} Thrive Beekeeping. Supporting backyard beekeepers worldwide.</p>
      </footer>
      <PublicFeatureNav current="/advertise" />
    </div>
  );
};

const AdvertiserDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');

  // Show public landing if not authenticated
  if (!token) return <AdvertiserLanding />;

  return <AdvertiserDashboardInner />;
};

const AdvertiserDashboardInner = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [pricing, setPricing] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState(1); // 1=creative, 2=targeting, 3=preview+pay
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchMyAds();
    fetchPricing();
    // Handle Stripe redirect
    const success = searchParams.get('success');
    const adId = searchParams.get('ad_id');
    const sessionId = searchParams.get('session_id');
    if (success === 'true' && adId && sessionId) {
      confirmPayment(adId, sessionId);
    }
  }, []);

  const fetchMyAds = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/advertiser/my-ads`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setAds(data.ads || []); }
    } catch {} finally { setLoading(false); }
  };

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${API}/api/ads/pricing`);
      if (res.ok) setPricing(await res.json());
    } catch {}
  };

  const confirmPayment = async (adId, sessionId) => {
    try {
      const res = await fetch(`${API}/api/advertiser/ads/${adId}/confirm-payment`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
      if (res.ok) { toast.success('Payment confirmed! Your ad is pending approval and will go live within 12 hours.'); fetchMyAds(); }
    } catch {}
  };

  const calculatePrice = async () => {
    try {
      const res = await fetch(`${API}/api/ads/calculate-price`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ placement: form.placement, geo_target: form.geo_target, duration_months: form.duration_months })
      });
      if (res.ok) setCalculatedPrice(await res.json());
    } catch {}
  };

  useEffect(() => { calculatePrice(); }, [form.placement, form.geo_target, form.duration_months]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/products/upload-image`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (res.ok) { const data = await res.json(); setForm(p => ({ ...p, image_url: `${API}/api/files/${data.path}` })); toast.success('Image uploaded'); }
      else toast.error('Upload failed');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.advertiser_name.trim()) { toast.error('Title and business name required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/advertiser/submit-ad`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowBuilder(false); setForm(emptyForm); setStep(1);
        fetchMyAds();
      } else {
        const err = await res.json(); toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const handleCheckout = async (adId) => {
    try {
      const res = await fetch(`${API}/api/advertiser/ads/${adId}/checkout`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      if (res.ok) { const data = await res.json(); window.location.href = data.checkout_url; }
      else { const err = await res.json(); toast.error(err.detail || 'Checkout failed'); }
    } catch { toast.error('Checkout failed'); }
  };

  const handlePause = async (adId) => {
    await fetch(`${API}/api/advertiser/ads/${adId}/pause`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    toast.success('Ad paused'); fetchMyAds();
  };

  const handleResume = async (adId) => {
    const res = await fetch(`${API}/api/advertiser/ads/${adId}/resume`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success('Ad resumed'); fetchMyAds(); }
    else { const err = await res.json(); toast.error(err.detail || 'Cannot resume'); }
  };

  const handleRenew = async (adId) => {
    const res = await fetch(`${API}/api/advertiser/ads/${adId}/renew`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ duration_months: 1 })
    });
    if (res.ok) { toast.success('Ad renewed — pending review'); fetchMyAds(); }
  };

  // Stats
  const activeCount = ads.filter(a => a.status === 'active').length;
  const totalImpressions = ads.reduce((s, a) => s + (a.impressions || 0), 0);
  const totalClicks = ads.reduce((s, a) => s + (a.clicks || 0), 0);
  const totalSpend = ads.filter(a => a.payment_status === 'paid').reduce((s, a) => s + (a.price_total || 0), 0);

  const imgUrl = (url) => url?.startsWith('http') ? url : `${API}${url}`;

  // Inline live preview component
  const LivePreviewInline = () => {
    if (!form.title && !form.advertiser_name) return null;
    return (
      <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 mt-3">
        <p className="text-[10px] text-stone-400 mb-2 font-medium uppercase tracking-wide">Live Preview</p>
        {form.placement === 'find_honey_sponsored' ? (
          <div className="bg-white rounded-xl border-2 border-amber-300 p-3 relative text-xs">
            <span className="absolute top-1 right-1 bg-amber-100 text-amber-700 text-[8px] font-bold px-1.5 py-0.5 rounded-full">Sponsored</span>
            <div className="flex items-start gap-3">
              {form.image_url && <img src={imgUrl(form.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" onError={e => e.target.style.display='none'} />}
              <div>
                <p className="font-bold text-stone-900">{form.advertiser_name || 'Business Name'}</p>
                <p className="text-stone-600">{form.title || 'Ad title'}</p>
                {form.description && <p className="text-stone-400 text-[10px] mt-0.5">{form.description}</p>}
                <span className="text-amber-700 font-semibold mt-1 inline-block">Visit Store →</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative rounded-lg overflow-hidden">
            {form.image_url ? (
              <img src={imgUrl(form.image_url)} alt="" className="w-full max-h-20 rounded-lg object-cover" onError={e => e.target.style.display='none'} />
            ) : (
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
                <div className="flex-1">
                  <p className="font-bold text-stone-800 text-xs">{form.advertiser_name || 'Business Name'}</p>
                  <p className="text-[10px] text-stone-600">{form.title || 'Ad title'}</p>
                </div>
                <span className="text-[10px] font-semibold text-amber-700">Visit →</span>
              </div>
            )}
            <span className="absolute top-1 right-1 bg-black/40 text-white text-[7px] px-1 py-0.5 rounded">Ad</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl" data-testid="advertiser-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-amber-600" /> Advertise on Thrive
          </h1>
          <p className="text-sm text-stone-500 mt-1">Reach beekeepers across Australia with targeted ads.</p>
        </div>
        <Button onClick={() => { setShowBuilder(true); setStep(1); setForm(emptyForm); }} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="create-ad-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Ad
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Active Ads', value: activeCount, icon: BarChart3, color: 'text-amber-600' },
          { label: 'Impressions', value: totalImpressions.toLocaleString(), icon: Eye, color: 'text-blue-600' },
          { label: 'Clicks', value: totalClicks.toLocaleString(), icon: MousePointer, color: 'text-green-600' },
          { label: 'Total Spend', value: `$${totalSpend.toFixed(2)}`, icon: DollarSign, color: 'text-purple-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-stone-200 p-4 text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-stone-500 flex items-center justify-center gap-1"><s.icon className="h-3 w-3" /> {s.label}</div>
          </div>
        ))}
      </div>

      {/* Pricing Reference */}
      <details className="mb-6 bg-white rounded-2xl border border-stone-200 overflow-hidden group" data-testid="pricing-panel">
        <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-stone-50 transition list-none">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-amber-600" />
            <span className="font-semibold text-stone-800">Pricing Guide</span>
            <span className="text-xs text-stone-400">— Placements, reach & duration discounts</span>
          </div>
          <span className="text-stone-400 text-xs group-open:rotate-90 transition-transform">▶</span>
        </summary>
        <div className="px-4 pb-5 border-t border-stone-100 pt-4 space-y-5">
          {/* Placement prices */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: 'Dashboard Banner', size: '728×90', price: 50, color: 'bg-amber-50 border-amber-200 text-amber-700' },
              { name: 'Sidebar Ad', size: '300×250', price: 35, color: 'bg-green-50 border-green-200 text-green-700' },
              { name: 'Sponsored Listing', size: 'Featured', price: 75, color: 'bg-purple-50 border-purple-200 text-purple-700' },
              { name: 'Mobile Banner', size: '320×50', price: 25, color: 'bg-blue-50 border-blue-200 text-blue-700' },
            ].map(p => (
              <div key={p.name} className={`rounded-xl border p-3 text-center ${p.color}`}>
                <div className="font-bold text-sm">{p.name}</div>
                <div className="text-[10px] opacity-60 mb-1">{p.size}</div>
                <div className="text-xl font-bold">${p.price}<span className="text-xs font-normal opacity-60">/mo</span></div>
              </div>
            ))}
          </div>
          {/* Multipliers + Discounts side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-stone-600 mb-2">Reach Multiplier</h4>
              <div className="space-y-1 text-xs">
                {[{ l: 'Local', v: '0.5×' }, { l: 'State', v: '0.75×' }, { l: 'National', v: '1.0×' }, { l: 'Global', v: '1.5×' }].map(r => (
                  <div key={r.l} className="flex justify-between bg-stone-50 rounded-lg px-3 py-1.5"><span>{r.l}</span><span className="font-bold">{r.v}</span></div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-stone-600 mb-2">Duration Discounts</h4>
              <div className="space-y-1 text-xs">
                {[{ l: '1 Month', v: 'Full price' }, { l: '3 Months', v: '10% off' }, { l: '6 Months', v: '20% off' }, { l: '12 Months', v: '30% off' }].map(d => (
                  <div key={d.l} className="flex justify-between bg-stone-50 rounded-lg px-3 py-1.5"><span>{d.l}</span><span className="font-bold">{d.v}</span></div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-[10px] text-stone-400 text-center">Example: Dashboard Banner + National + 6mo = $50 × 1.0 × 0.8 = <strong>$40/mo</strong> ($240 total)</p>
        </div>
      </details>

      {/* Ad Placement Visuals */}
      <details className="mb-6 bg-white rounded-2xl border border-stone-200 overflow-hidden group" data-testid="placement-visuals" open>
        <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-stone-50 transition list-none">
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-blue-600" />
            <span className="font-semibold text-stone-800">Where Your Ads Appear</span>
            <span className="text-xs text-stone-400">— Visual placement guide</span>
          </div>
          <span className="text-stone-400 text-xs group-open:rotate-90 transition-transform">▶</span>
        </summary>
        <div className="px-4 pb-5 border-t border-stone-100 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Dashboard Banner Mockup */}
            <div>
              <h4 className="text-xs font-semibold text-stone-600 mb-2 flex items-center gap-1"><BarChart3 className="h-3 w-3" /> Dashboard Banner — 728×90</h4>
              <div className="border border-stone-200 rounded-xl overflow-hidden bg-stone-50">
                <div className="bg-stone-100 px-3 py-1.5 text-[9px] text-stone-400 border-b flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-400 inline-block"></span>
                  <span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>
                  <span className="w-2 h-2 rounded-full bg-green-400 inline-block"></span>
                  <span className="ml-2">Thrive Dashboard</span>
                </div>
                {/* Banner position */}
                <div className="p-3">
                  <div className="bg-amber-100 border-2 border-dashed border-amber-400 rounded-lg h-[34px] flex items-center justify-center mb-2">
                    <span className="text-[10px] font-bold text-amber-600">YOUR AD HERE — 728×90</span>
                  </div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {[1,2,3,4,5].map(i => <div key={i} className="bg-stone-200 rounded h-7"></div>)}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                    {[1,2,3].map(i => <div key={i} className="bg-stone-200 rounded h-12"></div>)}
                  </div>
                </div>
                <div className="px-3 pb-2">
                  <p className="text-[9px] text-amber-700 font-medium">Top of every dashboard session. First thing beekeepers see.</p>
                </div>
              </div>
            </div>

            {/* Sidebar Mockup */}
            <div>
              <h4 className="text-xs font-semibold text-stone-600 mb-2 flex items-center gap-1"><Target className="h-3 w-3" /> Sidebar Ad — 300×250</h4>
              <div className="border border-stone-200 rounded-xl overflow-hidden bg-stone-50">
                <div className="bg-stone-100 px-3 py-1.5 text-[9px] text-stone-400 border-b flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-400 inline-block"></span>
                  <span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>
                  <span className="w-2 h-2 rounded-full bg-green-400 inline-block"></span>
                  <span className="ml-2">Hive Detail Page</span>
                </div>
                <div className="p-3 flex gap-2">
                  <div className="flex-1 space-y-1.5">
                    <div className="bg-stone-200 rounded h-5 w-3/4"></div>
                    <div className="bg-stone-200 rounded h-16"></div>
                    <div className="bg-stone-200 rounded h-10"></div>
                  </div>
                  <div className="w-24 flex-shrink-0">
                    <div className="bg-green-100 border-2 border-dashed border-green-400 rounded-lg h-20 flex items-center justify-center">
                      <span className="text-[8px] font-bold text-green-600 text-center leading-tight">YOUR AD<br/>300×250</span>
                    </div>
                  </div>
                </div>
                <div className="px-3 pb-2">
                  <p className="text-[9px] text-green-700 font-medium">Hive detail and inspection pages. Engaged, focused users.</p>
                </div>
              </div>
            </div>

            {/* Sponsored Listing Mockup */}
            <div>
              <h4 className="text-xs font-semibold text-stone-600 mb-2 flex items-center gap-1"><Award className="h-3 w-3" /> Sponsored Listing — Featured</h4>
              <div className="border border-stone-200 rounded-xl overflow-hidden bg-stone-50">
                <div className="bg-stone-100 px-3 py-1.5 text-[9px] text-stone-400 border-b flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-400 inline-block"></span>
                  <span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>
                  <span className="w-2 h-2 rounded-full bg-green-400 inline-block"></span>
                  <span className="ml-2">Find Local Honey</span>
                </div>
                <div className="p-3 space-y-1.5">
                  <div className="bg-purple-100 border-2 border-dashed border-purple-400 rounded-lg p-2 flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-200 rounded-lg flex-shrink-0"></div>
                    <div className="flex-1">
                      <div className="text-[9px] font-bold text-purple-700">YOUR BUSINESS — FEATURED</div>
                      <div className="text-[8px] text-purple-500">Pinned to the top of search results</div>
                    </div>
                    <span className="text-[7px] bg-purple-200 text-purple-700 px-1.5 py-0.5 rounded-full font-bold">SPONSORED</span>
                  </div>
                  {[1,2,3].map(i => <div key={i} className="bg-stone-200 rounded h-8"></div>)}
                </div>
                <div className="px-3 pb-2">
                  <p className="text-[9px] text-purple-700 font-medium">Top of honey directory. Buyers find you first.</p>
                </div>
              </div>
            </div>

            {/* Mobile Banner Mockup */}
            <div>
              <h4 className="text-xs font-semibold text-stone-600 mb-2 flex items-center gap-1"><Globe className="h-3 w-3" /> Mobile Banner — 320×50</h4>
              <div className="border border-stone-200 rounded-xl overflow-hidden bg-stone-50 max-w-[200px] mx-auto">
                <div className="bg-stone-100 px-2 py-1 text-[8px] text-stone-400 border-b flex items-center justify-between">
                  <span>9:41</span>
                  <span>Thrive</span>
                  <span>●●●</span>
                </div>
                <div className="p-2 space-y-1">
                  <div className="bg-stone-200 rounded h-4"></div>
                  <div className="grid grid-cols-2 gap-1">
                    {[1,2,3,4].map(i => <div key={i} className="bg-stone-200 rounded h-8"></div>)}
                  </div>
                </div>
                <div className="px-2 pb-1.5">
                  <div className="bg-blue-100 border-2 border-dashed border-blue-400 rounded h-5 flex items-center justify-center">
                    <span className="text-[8px] font-bold text-blue-600">YOUR AD — 320×50</span>
                  </div>
                </div>
                <div className="px-2 pb-2">
                  <p className="text-[9px] text-blue-700 font-medium text-center">Sticky mobile footer. Always visible.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </details>

      {/* My Ads */}
      {loading ? <p className="text-center py-12 text-stone-400">Loading...</p> : ads.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
          <BarChart3 className="h-12 w-12 text-stone-200 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-stone-700">No Ads Yet</h3>
          <p className="text-sm text-stone-500 mt-1 max-w-sm mx-auto">Create your first ad to reach thousands of beekeepers. Ads are reviewed and go live within 12 hours.</p>
          <Button onClick={() => setShowBuilder(true)} className="mt-4 bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="h-4 w-4 mr-2" /> Create Your First Ad
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {ads.map(ad => (
            <div key={ad.id} className="bg-white rounded-xl border border-stone-200 p-4" data-testid={`my-ad-${ad.id}`}>
              <div className="flex items-start gap-4">
                {ad.image_url ? (
                  <img src={imgUrl(ad.image_url)} alt="" className="w-20 h-14 rounded-lg object-cover flex-shrink-0 border" />
                ) : (
                  <div className="w-20 h-14 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0"><Eye className="h-5 w-5 text-stone-300" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-stone-900 text-sm">{ad.title}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[ad.status] || 'bg-stone-100 text-stone-500'}`}>
                      {STATUS_LABELS[ad.status] || ad.status}
                    </span>
                    {ad.payment_status === 'unpaid' && ad.status !== 'rejected' && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">UNPAID</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-stone-400 mt-1">
                    <span>{PLACEMENTS.find(p => p.id === ad.placement)?.label || ad.placement}</span>
                    <span>{ad.geo_target === 'global' ? 'Global' : ad.geo_state || ad.geo_country || ad.geo_target}</span>
                    {ad.duration_months && <span>{ad.duration_months}mo</span>}
                    {ad.price_total > 0 && <span className="font-medium text-stone-600">${ad.price_total}</span>}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
                    <span><Eye className="h-3 w-3 inline mr-1" />{(ad.impressions || 0).toLocaleString()}</span>
                    <span><MousePointer className="h-3 w-3 inline mr-1" />{(ad.clicks || 0).toLocaleString()}</span>
                    {ad.start_date && <span>{ad.start_date}{ad.end_date ? ` → ${ad.end_date}` : ''}</span>}
                  </div>
                  {ad.rejection_reason && <p className="text-xs text-red-600 mt-1">Reason: {ad.rejection_reason}</p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {ad.payment_status === 'unpaid' && ad.status !== 'rejected' && (
                    <Button size="sm" onClick={() => handleCheckout(ad.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8" data-testid={`pay-${ad.id}`}>
                      <DollarSign className="h-3.5 w-3.5 mr-1" /> Pay ${ad.price_total}
                    </Button>
                  )}
                  {ad.status === 'active' && <Button size="sm" variant="ghost" onClick={() => handlePause(ad.id)} className="h-8 w-8 p-0" title="Pause"><Pause className="h-4 w-4 text-yellow-600" /></Button>}
                  {ad.status === 'paused' && <Button size="sm" variant="ghost" onClick={() => handleResume(ad.id)} className="h-8 w-8 p-0" title="Resume"><Play className="h-4 w-4 text-green-600" /></Button>}
                  {(ad.status === 'expired' || ad.status === 'rejected') && <Button size="sm" variant="ghost" onClick={() => handleRenew(ad.id)} className="h-8 w-8 p-0" title="Renew"><RefreshCw className="h-4 w-4 text-amber-600" /></Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ad Builder Dialog */}
      <Dialog open={showBuilder} onOpenChange={setShowBuilder}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Your Ad — Step {step} of 3</DialogTitle>
          </DialogHeader>

          {/* Step indicators */}
          <div className="flex items-center gap-2 mb-4">
            {['Creative', 'Targeting', 'Preview & Submit'].map((s, i) => (
              <div key={s} className={`flex-1 text-center py-1.5 rounded-lg text-xs font-medium ${i + 1 === step ? 'bg-amber-100 text-amber-800' : i + 1 < step ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-400'}`}>
                {i + 1 < step ? <Check className="h-3 w-3 inline mr-1" /> : null}{s}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Ad Title *</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Spring Sale — 20% Off Hive Equipment" data-testid="ad-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Business Name *</Label><Input value={form.advertiser_name} onChange={e => setForm({...form, advertiser_name: e.target.value})} placeholder="Your business name" data-testid="ad-biz-name" /></div>
                <div><Label>Website URL</Label><Input value={form.advertiser_url} onChange={e => setForm({...form, advertiser_url: e.target.value})} placeholder="https://..." /></div>
              </div>
              <div>
                <Label>Banner Image</Label>
                <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-stone-300 rounded-xl p-4 cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors mt-1 ${uploading ? 'opacity-50' : ''}`}>
                  <Upload className="h-5 w-5 text-stone-400" />
                  <span className="text-sm text-stone-500">{uploading ? 'Uploading...' : 'Click to upload image'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                </label>
                {form.image_url && <img src={imgUrl(form.image_url)} alt="Preview" className="mt-2 rounded-lg border max-h-32 object-contain" />}
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Short description of your product or offer" className="min-h-[60px]" />
              </div>
              <LivePreviewInline />
              <div className="flex justify-end">
                <Button onClick={() => { if (!form.title || !form.advertiser_name) { toast.error('Title and business name required'); return; } setStep(2); }} className="bg-amber-600 hover:bg-amber-700 text-white">Next: Targeting</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label>Ad Placement</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {PLACEMENTS.map(p => (
                    <button key={p.id} onClick={() => setForm({...form, placement: p.id})} className={`p-3 rounded-xl border-2 text-left transition-all ${form.placement === p.id ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-300'}`}>
                      <div className="text-sm font-medium text-stone-800">{p.label}</div>
                      <div className="text-[11px] text-stone-400 mt-0.5">{p.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Geo Targeting</Label>
                <Select value={form.geo_target} onValueChange={v => setForm({...form, geo_target: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{GEO_OPTIONS.map(g => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.geo_target === 'state' && (
                <div><Label>State / Region</Label><Input value={form.geo_state} onChange={e => setForm({...form, geo_state: e.target.value})} placeholder="e.g., Victoria" /></div>
              )}
              {form.geo_target === 'national' && (
                <div><Label>Country</Label><Input value={form.geo_country} onChange={e => setForm({...form, geo_country: e.target.value})} placeholder="e.g., Australia" /></div>
              )}
              <div>
                <Label>Duration</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {DURATION_OPTIONS.map(d => (
                    <button key={d.months} onClick={() => setForm({...form, duration_months: d.months})} className={`p-2.5 rounded-lg border-2 text-sm font-medium transition-all ${form.duration_months === d.months ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Price */}
              {calculatedPrice && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-amber-700">${calculatedPrice.total}</div>
                  <div className="text-xs text-amber-600 mt-1">{calculatedPrice.breakdown}</div>
                  {calculatedPrice.discount_percent > 0 && <div className="text-xs text-green-600 font-medium mt-1">You save {calculatedPrice.discount_percent}%!</div>}
                </div>
              )}
              <LivePreviewInline />
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={() => setStep(3)} className="bg-amber-600 hover:bg-amber-700 text-white">Next: Preview</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-stone-700">Live Preview</h4>
              {/* Live Preview */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
                {form.placement === 'find_honey_sponsored' ? (
                  <div className="bg-white rounded-2xl border-2 border-amber-300 p-5 relative">
                    <span className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full">Sponsored</span>
                    <div className="flex items-start gap-4">
                      {form.image_url && <img src={imgUrl(form.image_url)} alt="" className="w-20 h-20 rounded-xl object-cover" />}
                      <div>
                        <h4 className="font-bold text-stone-900 text-sm">{form.advertiser_name || 'Your Business'}</h4>
                        <p className="text-sm text-stone-600 mt-0.5">{form.title || 'Your ad title'}</p>
                        {form.description && <p className="text-xs text-stone-400 mt-1">{form.description}</p>}
                        <span className="inline-block mt-2 text-xs font-semibold text-amber-700">Visit Store →</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative rounded-xl overflow-hidden">
                    {form.image_url ? (
                      <img src={imgUrl(form.image_url)} alt="" className="w-full h-auto rounded-xl" />
                    ) : (
                      <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4 flex items-center gap-4">
                        <div className="flex-1">
                          <p className="font-bold text-stone-800 text-sm">{form.advertiser_name || 'Your Business'}</p>
                          <p className="text-xs text-stone-600 mt-0.5">{form.title || 'Your ad title'}</p>
                        </div>
                        <span className="text-xs font-semibold text-amber-700">Visit →</span>
                      </div>
                    )}
                    <span className="absolute top-1.5 right-1.5 bg-black/40 text-white text-[9px] font-medium px-1.5 py-0.5 rounded">Ad</span>
                  </div>
                )}
                <p className="text-[11px] text-stone-400 text-center mt-2">This is how your ad will appear to beekeepers</p>
              </div>

              {/* Order Summary */}
              <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-2">
                <h4 className="text-sm font-semibold text-stone-700">Order Summary</h4>
                <div className="flex justify-between text-sm"><span className="text-stone-500">Placement</span><span className="font-medium">{PLACEMENTS.find(p => p.id === form.placement)?.label}</span></div>
                <div className="flex justify-between text-sm"><span className="text-stone-500">Targeting</span><span className="font-medium">{form.geo_target === 'state' ? form.geo_state : form.geo_target === 'national' ? form.geo_country : form.geo_target}</span></div>
                <div className="flex justify-between text-sm"><span className="text-stone-500">Duration</span><span className="font-medium">{form.duration_months} month(s)</span></div>
                <div className="border-t pt-2 flex justify-between text-base font-bold"><span>Total</span><span className="text-amber-700">${calculatedPrice?.total || '—'}</span></div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
                <Clock className="h-4 w-4 inline mr-1" />
                Your ad will be reviewed by our team. If approved, it will go live within <strong>12 hours</strong>. You'll receive an email confirmation.
              </div>

              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
                <Button onClick={handleSubmit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="submit-ad-btn">
                  {submitting ? 'Submitting...' : 'Submit Ad for Review'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvertiserDashboard;
